import {
  ButtonToCard,
  CheckBox,
  DropDown,
  Tab,
} from "@etframework/ui.components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInitialRender } from "../../hooks/useInitialRender";
import useOrdinal from "../../hooks/useOrdinal";
import Enums from "../../types/enums";
import { WeekdaysContainer } from "./WeekdaysContainer";
import { YearCalculationContainer } from "./YearCalculationContainer";

export const DueDateRuleContent = ({
  id,
  taskType,
  initialValue = null,
  onChange,
}) => {
  const contentRef = useRef();
  const tabsRef = useRef();
  const isInitialRender = useInitialRender();

  const { t, i18n } = useTranslation(["TaskTemplateModal"], {
    useSuspense: false,
  });

  const currentLanguage = i18n.language;
  const {
    dayData,
    monthData,
    monthDataForExtendedDate,
    monthlyWordData,
    yearData,
  } = useOrdinal(currentLanguage);
  const fields = {
    text: "text",
    value: "value",
  };

  const ids = {
    monthly: {
      day: id + "_monthly-rule_day-dropdown",
      month: id + "_monthly-rule_month-dropdown",
    },
    quarterly: {
      day: id + "_quertely-rule_day-dropdown",
      month: id + "_quertely-rule_month-dropdown",
    },
    annual: {
      day: id + "_annual-rule_day-dropdown",
      month: id + "_annual-rule_month-dropdown",
      year: id + "_annual-rule_financial-year-dropdown",
    },
    scheduleOnWeekdays: id + "_schedule-on-weekdays",
    duedateCalculation: id + "_year-calculation",
    duedateExtension: {
      checkbox: id + "_set-duedate-extension_checkbox",
      dropdown: id + "_set-duedate-extension_month-dropdown",
    },
  };

  const [dueDateRuleObj, setDueDateRuleObj] = useState(
    initialValue || {
      DateFrequency: Enums.DateFrequency.Annual,
      Day: 1,
      Month: 1,
      Year: Enums.FinancialYear.CalendarYear,
      ExtensionMonth: null,
      ScheduleWeekdays: true,
      WeekdayBehavior: Enums.WeekdayBehavior.AfterWeekend,
      DueDateYearCalculation:
        taskType?.Key === "TAX"
          ? Enums.DueDateYearCalculationTypes.PeriodEnd
          : Enums.DueDateYearCalculationTypes.PeriodStart,
    }
  );
  const [enableExtensionRule, setEnableExtensionRule] = useState(
    initialValue?.ExtensionMonth != null ? true : false
  );

  const onDueDateRuleChange = (value, key) => {
    setDueDateRuleObj((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onDueDateRuleWeekdayChange = useCallback((value) => {
    setDueDateRuleObj((prevState) => ({
      ...prevState,
      ScheduleWeekdays: value?.ScheduleWeekdays || false,
      WeekdayBehavior: value?.ScheduleWeekdays ? value?.WeekdayBehavior : null,
    }));
  }, []);

  const onDueDateRuleYearCalculationChange = useCallback((value) => {
    setDueDateRuleObj((prevState) => ({
      ...prevState,
      DueDateYearCalculation:
        value?.DueDateYearCalculation ||
        Enums.DueDateYearCalculationTypes.PeriodEnd,
    }));
  }, []);

  const removeCardHandler = () => {
    contentRef.current.setSkipCloseConfirmation(true);
    onChange?.(null);
  };

  // this function updates multiple fields in one state change
  const onChangeFrequency = (frequency) => {
    setDueDateRuleObj((prevState) => {
      let updates = {
        Day: 1,
        Month: 1,
        Year: Enums.FinancialYear.CalendarYear,
      };

      switch (frequency) {
        case Enums.DateFrequency.Quarterly: {
          updates = {
            ...updates,
            Year: null,
          };
          break;
        }
        case Enums.DateFrequency.Monthly: {
          updates = {
            ...updates,
            Year: null,
            Month: null,
          };
          break;
        }
      }

      updates = {
        ...updates,
        DateFrequency: frequency,
      };

      return { ...prevState, ...updates };
    });
  };

  useEffect(() => {
    onChange?.(!initialValue ? null : dueDateRuleObj);
  }, [initialValue]);

  useEffect(() => {
    if (isInitialRender && !initialValue) {
      onChange?.(null);
    } else {
      onChange?.(dueDateRuleObj);
    }
  }, [dueDateRuleObj]);

  const DayDropDown = ({ id }) => {
    return (
      <DropDown
        value={dueDateRuleObj?.Day}
        dataSource={dayData.map((x) => {
          if (x?.value == 31)
            return { text: t("DateContainers.LastDay"), value: 31 };
          else return x;
        })}
        fields={fields}
        htmlFor={id}
        id={id}
        locale="en"
        change={(e) => {
          onDueDateRuleChange(e.value, "Day");
        }}
      />
    );
  };

  const MonthDropdown = ({ id }) => {
    return (
      <DropDown
        value={dueDateRuleObj?.Month ?? 2}
        dataSource={monthData}
        fields={fields}
        htmlFor={id}
        id={id}
        locale="en"
        change={(e) => {
          onDueDateRuleChange(e.value, "Month");
        }}
      />
    );
  };

  const MonthlyDropdown = ({ id }) => {
    return (
      <DropDown
        value={dueDateRuleObj?.Month ?? 1}
        dataSource={monthlyWordData}
        fields={fields}
        htmlFor={id}
        id={id}
        locale="en"
        change={(e) => {
          onDueDateRuleChange(e.value, "Month");
        }}
      />
    );
  };

  const AnnualDropdown = ({ id }) => {
    return (
      <DropDown
        value={dueDateRuleObj?.Year}
        dataSource={yearData}
        fields={fields}
        htmlFor={id}
        id={id}
        locale="en"
        change={(e) => {
          onDueDateRuleChange(e.value, "Year");
        }}
      />
    );
  };

  const AnnualContainer = () => {
    return (
      <>
        <div className="row">
          <div className="d-flex space-x-2 align-items-center col mt-4 p-4">
            <span>{t("DateContainers.DueOnThe")}</span>
            <DayDropDown id={ids.annual.day} />
            <span>{t("DateContainers.OfThe")}</span>
            <MonthDropdown id={ids.annual.month} />
            {taskType?.Key === "TAX" ? (
              <div className="d-flex align-items-center">
                {dueDateRuleObj?.Year == 0 ? (
                  <span>{t("DateContainers.OnClosing")}</span>
                ) : (
                  <span>{t("DateContainers.InTheMonth")}</span>
                )}
                <AnnualDropdown id={ids.annual.year} />
              </div>
            ) : (
              <div>
                <span>{t("DateContainers.InTheCalendarYear")}</span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const QuarterlyContainer = () => {
    return (
      <>
        <div className="row">
          <div className="d-flex space-x-2 align-items-center col mt-4 p-4">
            <span>{t("DateContainers.FirstQuarterDue")}</span>
            <DayDropDown id={ids.quarterly.day} />
            <span>{t("DateContainers.OfThe")}</span>
            <MonthDropdown id={ids.quarterly.month} />
            <span>{t("DateContainers.OfTheCalendarYear")}</span>
          </div>
          <div className="px-4 pb-3 fs-12">
            {t("DateContainers.QuarterNote")}
          </div>
        </div>
      </>
    );
  };

  const MonthlyContainer = () => {
    return (
      <>
        <div className="row">
          <div className="d-flex space-x-2 align-items-center col mt-4 p-4">
            <span>{t("DateContainers.DueOnThe")}</span>
            <DayDropDown id={ids.monthly.day} />
            <span>{t("DateContainers.OfEveryMonth")}</span>
            <MonthlyDropdown id={ids.monthly.month} />
          </div>
          <div className="px-4 pb-3 fs-12">
            {t("DateContainers.MonthlyNote")}
          </div>
        </div>
      </>
    );
  };

  const DueDateExtensionContent = () => {
    return (
      <div className="col mt-2 p-4">
        <div className="row weekends-chk">
          <CheckBox
            id={ids.duedateExtension.checkbox}
            checked={enableExtensionRule}
            label={t("DueDateExtensionContent.SetDueDateExtensionRule")}
            change={(e) => {
              setEnableExtensionRule((prev) => !prev);
              if (e.checked) onDueDateRuleChange(6, "ExtensionMonth");
              else onDueDateRuleChange(null, "ExtensionMonth");
            }}
          />
        </div>
        {enableExtensionRule && (
          <div className="row mt-3">
            <div className="d-flex col ms-2 p-2 extensionRule-radBtn space-y-2">
              <span className="align-self-center m-2">
                {t("DueDateExtensionContent.ExtendBy")}
              </span>
              <DropDown
                value={dueDateRuleObj?.ExtensionMonth}
                dataSource={monthDataForExtendedDate}
                fields={fields}
                id={ids.duedateExtension.dropdown}
                htmlFor={ids.duedateExtension.dropdown}
                locale="en"
                change={(e) => {
                  onDueDateRuleChange(e.value, "ExtensionMonth");
                }}
              />
              <span className="align-self-center m-2">
                {t("DueDateExtensionContent.MonthAfter")}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const CardContent = () => {
    return (
      <div className="card-content">
        <div className="row">
          <Tab
            ref={tabsRef}
            id="due-date-rule-tabs"
            activeTab={dueDateRuleObj?.DateFrequency}
            tabNames={[
              t("DueDateRuleContent.AnnualTab"),
              t("DueDateRuleContent.QuarterlyTab"),
              t("DueDateRuleContent.MonthlyTab"),
            ]}
            tabComponents={[
              <AnnualContainer key={0} />,
              <QuarterlyContainer key={1} />,
              <MonthlyContainer key={2} />,
            ]}
            typeOfTab="purchase"
            reorderActiveTab={false}
            onChange={(e) => {
              onChangeFrequency(e);
            }}
          />
        </div>
        {taskType?.Type === Enums.TaskType.TaxReturn && (
          <>
            <hr />
            <div className="row">
              <DueDateExtensionContent />
            </div>
            <hr />
          </>
        )}

        <div className="row">
          <WeekdaysContainer
            id={ids.scheduleOnWeekdays}
            name="duedates-rule-weekdays"
            onChange={onDueDateRuleWeekdayChange}
            initialValue={{
              ScheduleWeekdays: dueDateRuleObj?.ScheduleWeekdays,
              WeekdayBehavior: dueDateRuleObj?.WeekdayBehavior,
            }}
          />
        </div>

        {taskType?.Type === Enums.TaskType.TaxReturn && (
          <>
            <hr />
            <div className="row">
              <YearCalculationContainer
                id={ids.duedateCalculation}
                name="duedates-rule-year-calculation"
                onChange={onDueDateRuleYearCalculationChange}
                initialValue={{
                  DueDateYearCalculation:
                    dueDateRuleObj?.DueDateYearCalculation,
                }}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <ButtonToCard
      open={initialValue !== null}
      text={t("DueDateRuleContent.BtnText")}
      isOptional={false}
      showButton={false}
      cardTitle={t("DueDateRuleContent.CardTitle")}
      lang={currentLanguage}
      closeModalContent={() => {
        onChange?.(dueDateRuleObj);
      }}
      iconCss={`fa-regular fa-plus`}
      isContentLeft
      ref={contentRef}
      beforeClose={removeCardHandler}
    >
      <CardContent />
    </ButtonToCard>
  );
};
